import { RouteFactory } from "@app/routePaths";
import type { NavigationItem } from "@app/types/Navigation";
import epaperAltIcon from "@assets/footer/icon-epaper-alt.svg";
import logoAB from "@assets/footer/logo-asean-business.svg";
import logoBTLifeStyle from "@assets/footer/logo-bt-lifestyle.svg";
import logoBTLuxe from "@assets/footer/logo-bt-luxe.svg";
import logoGarage from "@assets/footer/logo-garage.svg";
import logoGE from "@assets/footer/logo-global-enterprise.svg";
import logoSGSME from "@assets/footer/logo-sg-sme.svg";
import logoWM from "@assets/footer/logo-wealth-magazine.svg";
import defaultIconAppGoogle from "@assets/icon-app-google.svg";
import defaultIconAppIOS from "@assets/icon-app-ios.svg";
import type {
  CopyrightItem,
  LogoItem,
  SocialLinkConfig,
} from "@components/Footer/types";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faNewspaper } from "@fortawesome/free-regular-svg-icons";
import {
  faListDots,
  faPaperPlane,
  faRss,
} from "@fortawesome/free-solid-svg-icons";
import {
  EPAPER_API_ENDPOINT,
  ESHOP_SUBSCRIBE_CTA_FOOTER,
  ESHOP_SUBSCRIBE_CTA_HEADER,
  sectionNavigationItems,
} from "@util/constant";
import dayjs from "dayjs";

const appDownloadLinks = {
  ios: {
    src: defaultIconAppIOS,
    link: "https://itunes.apple.com/sg/app/business-times-for-iphone/id531275824",
    alt: "Download on the Apple Store",
  },
  google: {
    src: defaultIconAppGoogle,
    link: "https://play.google.com/store/apps/details?id=com.ps.bt&hl=en",
    alt: "Download on the Google Play Store",
  },
};

const socialLinks: Record<string, SocialLinkConfig> = {
  fb: {
    icon: faFacebookF,
    link: "https://www.facebook.com/thebusinesstimes",
    alt: "Share on Facebook",
  },
  fbBlack: {
    icon: faFacebookF,
    link: "https://www.facebook.com/thebusinesstimes",
    alt: "Share on Facebook",
  },
  twitter: {
    icon: faXTwitter,
    link: "https://twitter.com/businesstimes",
    alt: "Share on Twitter",
  },
  linkedin: {
    icon: faLinkedinIn,
    link: "https://www.linkedin.com/showcase/the-business-times",
    alt: "Share on Linkedin",
  },
  telegram: {
    icon: faPaperPlane,
    link: "https://t.me/BizTimes",
    alt: "Share on Telegram",
  },
  rss: {
    icon: faRss,
    link: "/rss-feeds",
    alt: "Subscribe to RSS",
  },
  ig: {
    icon: faInstagram,
    link: "https://www.instagram.com/businesstimessg",
    alt: "Share on Instagram",
  },
  epaper: {
    icon: faNewspaper,
    link: `${EPAPER_API_ENDPOINT}/`,
    alt: "Subscribe to Epaper",
    label: "E-PAPER",
  },
  epaperBTArchive: {
    icon: faNewspaper,
    link: `${EPAPER_API_ENDPOINT}/jr/?archive=open&tour=close`,
    alt: "Subscribe to Epaper",
    label: "BT 12-DAY PRINT ARCHIVE",
  },
  whatsapp: {
    icon: faWhatsapp,
    link: "https://wa.me/?text=THE%20BUSINESS%20TIMES%20-%20Get%20the%20Latest%20Business%20%26%20Financial%20News%20%0D%0Ahttps%3A%2F%2Fwww.businesstimes.com.sg",
  },
  more: { icon: faListDots, link: "" },
};

const navigationItems: Record<
  string,
  Pick<NavigationItem, "link" | "label">
> = {
  "about-us": {
    label: "about us",
    link: RouteFactory.aboutUs,
  },
  "contact-us": {
    label: "contact us",
    link: RouteFactory.contactUs,
  },
  advertise: {
    label: "advertise",
    link: RouteFactory.advertise,
  },
  pr: {
    label: "Paid Press Releases",
    link: RouteFactory.pr,
  },
  "advertise-with-us": {
    label: "advertise with us",
    link: "https://www.sph.com.sg/advertising-solutions/",
  },
  help: {
    label: "help",
    link: RouteFactory.help,
  },
  "privacy-policy": {
    label: "privacy policy",
    link: "https://www.sph.com.sg/legal/sph_privacy/",
  },
  "terms-conditions": {
    label: "terms & conditions",
    link: "https://www.sph.com.sg/legal/website_tnc/",
  },
  "cookie-policy": {
    label: "cookie policy",
    link: "https://www.sph.com.sg/legal/cookie-policy/",
  },
  "data-protection-policy": {
    label: "data protection policy",
    link: "https://www.sph.com.sg/legal/pdpa/",
  },
};

const logoLinks: Record<string, LogoItem> = {
  ab: {
    link: "/international/asean",
    src: logoAB,
    alt: "Asean Business",
  },
  garage: {
    link: "/startups-tech/startups",
    src: logoGarage,
    alt: "Garage",
  },
  ge: {
    link: "/international/global",
    src: logoGE,
    alt: "Global Eneterprise",
  },
  sgsme: {
    link: "/singapore/smes",
    src: logoSGSME,
    alt: "SGSME",
  },
  wealth: {
    link: "/wealth",
    src: logoWM,
    alt: "Wealth",
  },
  bt: {
    link: "/lifestyle",
    src: logoBTLifeStyle,
    alt: "BT Lifestyle",
  },
  btluxe: {
    link: "/lifestyle/bt-luxe",
    src: logoBTLuxe,
    alt: "BT Luxe",
  },
};

const footerLogos = [
  logoLinks.ab,
  logoLinks.garage,
  logoLinks.ge,
  logoLinks.sgsme,
  logoLinks.wealth,
  logoLinks.bt,
  logoLinks.btluxe,
];

const footerSocialLinksMobile = [
  socialLinks.fb,
  socialLinks.twitter,
  socialLinks.linkedin,
  socialLinks.telegram,
  socialLinks.rss,
  socialLinks.ig,
  { ...socialLinks.epaper, src: epaperAltIcon },
];

const footerSocialLinks = [
  socialLinks.fb,
  socialLinks.rss,
  socialLinks.ig,
  socialLinks.twitter,
  socialLinks.linkedin,
  socialLinks.telegram,
];

const footerNavigationItems = [
  navigationItems["about-us"],
  navigationItems["contact-us"],
  navigationItems["advertise"],
  navigationItems["pr"],
  navigationItems["advertise-with-us"],
  navigationItems.help,
  navigationItems["privacy-policy"],
  navigationItems["terms-conditions"],
  navigationItems["cookie-policy"],
  navigationItems["data-protection-policy"],
];

const footerSectionNavigationTitles = ["Sections", null, "Lifestyle"];

const footerSectionNavigationItems = [
  [
    sectionNavigationItems.singapore,
    sectionNavigationItems.international,
    sectionNavigationItems["opinion-features"],
    sectionNavigationItems["companies-markets"],
    sectionNavigationItems.property,
    sectionNavigationItems["startups-tech"],
    sectionNavigationItems.esg,
    sectionNavigationItems.wealth,
  ] as NavigationItem[],
  [
    sectionNavigationItems["working-life"],
    sectionNavigationItems["events-awards"],
    sectionNavigationItems["breaking-news"],
    sectionNavigationItems.epaper,
    sectionNavigationItems.podcasts,
    sectionNavigationItems.videos,
    sectionNavigationItems.newsletter,
  ] as NavigationItem[],
];

const footerLifestyleNavigationItems = [
  sectionNavigationItems["keywords/food-drink"],
  sectionNavigationItems["keywords/motoring"],
  sectionNavigationItems["keywords/style-travel"],
  sectionNavigationItems["keywords/watches"],
  sectionNavigationItems["keywords/art-design"],
  sectionNavigationItems["lifestyle/bt-luxe"],
  sectionNavigationItems["keywords/health-wellness"],
] as NavigationItem[];

const copyright: CopyrightItem = {
  title: "SPH MEDIA DIGITAL NEWS",
  content: `MCI (P) 064/10/2023 © ${dayjs().format(
    "YYYY"
  )} SPH MEDIA LIMITED. REGN NO. 202120748H`,
};

const subscribeCTALink = {
  header: `${ESHOP_SUBSCRIBE_CTA_HEADER}`,
  footer: `${ESHOP_SUBSCRIBE_CTA_FOOTER}`,
};

export {
  appDownloadLinks,
  copyright,
  footerLifestyleNavigationItems,
  footerLogos,
  footerNavigationItems,
  footerSectionNavigationItems,
  footerSectionNavigationTitles,
  footerSocialLinks,
  footerSocialLinksMobile,
  logoLinks,
  navigationItems,
  socialLinks,
  subscribeCTALink,
};
